import {
  useProfileData,
  useStudentRankingData,
  useTopOneData,
} from "@/queries/queries"
import { Dropdown } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import {
  FaFacebookF,
  FaLink,
  FaLinkedinIn,
  FaShareAlt,
  FaStar,
  FaTrophy,
  FaTwitter,
} from "react-icons/fa"
import { toast } from "react-toastify"

export default function AchievementFlair() {
  const { t } = useTranslation()
  const profileQuery = useProfileData()
  const topOneQuery = useTopOneData()
  const rankingQuery = useStudentRankingData()

  const BASE_URL = "https://backendlms.eraasoft.online"

  if (!profileQuery.data || !topOneQuery.data || !rankingQuery.data) return null

  const userId = profileQuery.data.id
  const myAchievement = topOneQuery.data.achievements.find(
    (s) => s.user.id === userId
  )
  const isTopOne = !!myAchievement

  // Find current round ranking
  const currentRoundRanking = rankingQuery.data?.[0] // Assuming first round in list is current or active
  const isRoundLeader =
    currentRoundRanking?.top_students?.[0]?.student_id === userId

  if (!isTopOne && !isRoundLeader) return null

  const getShareUrl = () => {
    if (isTopOne && myAchievement) {
      return `${BASE_URL}/achievements/top-one/${myAchievement.id}`
    }
    return window.location.origin
  }

  const handleShare = (
    platform: "facebook" | "twitter" | "linkedin" | "copy"
  ) => {
    const url = getShareUrl()
    const course = topOneQuery.data.course.title
    const group = currentRoundRanking?.round_name || ""

    const message = isRoundLeader
      ? t("achievementFlair.shareMessageRoundLeader", { course, group })
      : t("achievementFlair.shareMessageTopOne", { course, group })

    if (platform === "copy") {
      navigator.clipboard.writeText(`${message} ${url}`)
      toast.success(
        t(
          "achievementFlair.linkCopied",
          "Link and message copied to clipboard!"
        )
      )
      return
    }

    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(message)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}&url=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    }

    window.open(shareUrls[platform], "_blank", "width=600,height=400")
  }

  const sharePlatforms = [
    {
      key: "copy" as const,
      icon: <FaLink className="text-secondary" />,
      label: t("buttons.copyLink"),
    },
    {
      key: "facebook" as const,
      icon: <FaFacebookF className="text-primary" />,
      label: t("buttons.facebook"),
    },
    {
      key: "twitter" as const,
      icon: <FaTwitter className="text-info" />,
      label: t("buttons.twitter"),
    },
    {
      key: "linkedin" as const,
      icon: <FaLinkedinIn style={{ color: "#0077b5" }} />,
      label: t("buttons.linkedin"),
    },
  ]

  return (
    <div
      className="p-3 mb-4 rounded-4 d-flex align-items-center justify-content-between"
      style={{
        background: "linear-gradient(90deg, #243b78 0%, #3a5ba0 100%)",
        color: "white",
        boxShadow: "0 4px 15px rgba(36, 59, 120, 0.2)",
        border: "1px solid rgba(255, 255, 255, 0.1)",
      }}
    >
      <div className="d-flex align-items-center gap-3">
        <div
          className="rounded-circle bg-white bg-opacity-25 d-flex align-items-center justify-content-center"
          style={{ width: "45px", height: "45px" }}
        >
          {isRoundLeader ? (
            <FaTrophy size={22} color="#ffd700" />
          ) : (
            <FaStar size={22} color="#ffd700" />
          )}
        </div>
        <div>
          <h6 className="m-0 fw-bold">
            {isRoundLeader
              ? t("achievementFlair.roundLeaderTitle")
              : t("achievementFlair.topOneTitle")}
          </h6>
          <p className="m-0 small opacity-75">
            {isRoundLeader
              ? t("achievementFlair.roundLeaderSub")
              : t("achievementFlair.topOneSub")}
          </p>
        </div>
      </div>
      <div className="d-flex align-items-center gap-2">
        <div className="d-none d-md-block">
          <div className="badge bg-white text-primary rounded-pill px-3 py-2">
            {isRoundLeader
              ? t("achievementFlair.roundLeaderBadge")
              : t("achievementFlair.topOneBadge")}
          </div>
        </div>

        <Dropdown align="end">
          <Dropdown.Toggle
            variant="light"
            size="sm"
            className="rounded-pill d-flex align-items-center gap-2 border-0 shadow-sm"
            style={{
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              color: "white",
            }}
          >
            <FaShareAlt />
            <span className="d-none d-sm-inline">{t("buttons.share")}</span>
          </Dropdown.Toggle>

          <Dropdown.Menu className="border-0 shadow-lg rounded-3">
            {sharePlatforms.map(({ key, icon, label }) => (
              <Dropdown.Item
                key={key}
                onClick={() => handleShare(key)}
                className="d-flex align-items-center gap-2 py-2"
              >
                {icon} {label}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </div>
  )
}
